import { Page } from "@/types/Page";
import {
  createPages,
  duplicatePage as duplicatePageApi,
  getPage as getPageApi,
  getPageBySlug as getPageBySlugApi,
  getPages as getPagesApi,
  updatePage as updatePageApi,
  deletePage as deletePageApi,
} from "@/lib/api/clients/Pages";
import { handleErrorServer, ServerError } from "@/lib/api/errors/ErrorHandler";
import { isValidObjectId } from "@/lib/utils/input-validator";
import { getWebsiteConfig } from "./WebsiteConfigHandling";
import { WebsiteConfig } from "@/types/WebsiteConfig";
import { getFields } from "./ViewFilterHandling";
import { Field } from "@/types/Field";
import { getS3PictureUrls } from "./S3Handling";

/**
 * Create pages with validation checks
 * @param eventId Event ID
 * @param pages Array of pages to create
 * @returns Promise that resolves to array of created page IDs or a ServerError
 */
export async function createPagesChecked(
  eventId: string,
  pages: Page[],
): Promise<string[] | ServerError> {
  try {
    // Input validation
    if (
      !eventId ||
      !pages ||
      !isValidObjectId(eventId) ||
      pages?.length === 0
    ) {
      throw new Error("validationError");
    }

    // Validate each page
    pages.forEach((page) => {
      if (!page.pageName) throw new Error("validationError");
      if (!page.slug) throw new Error("validationError");
      if (!page.websiteJson) throw new Error("validationError");

      // Check for slug format - must start with a slash followed by alphanumeric characters, hyphens, or underscores
      if (page.slug && !/^\/[a-z0-9\-_\/]*$/.test(page.slug))
        throw new Error("validationError");
    });

    // Check for duplicate slugs within the pages array
    const slugs = new Set<string>();
    const duplicateSlugs = pages
      .map((page) => page.slug)
      .filter((slug) => {
        if (slugs.has(slug!)) {
          return true;
        }
        slugs.add(slug!);
        return false;
      });

    if (duplicateSlugs?.length > 0) throw new Error("validationError");


    // Get the actual creating pages call
    return await createPages(eventId, pages);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Duplicate a page via the backend duplicate endpoint
 * @param eventId Event ID
 * @param pageId Page ID to duplicate
 * @returns Promise resolving to the created page ID or a ServerError
 */
export async function duplicatePage(
  eventId: string,
  pageId: string,
): Promise<string | ServerError> {
  try {
    if (
      !eventId ||
      !pageId ||
      !isValidObjectId(eventId) ||
      !isValidObjectId(pageId)
    ) {
      throw new Error("validationError");
    }

    return await duplicatePageApi(eventId, pageId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Delete a page by ID
 * @param eventId Event ID
 * @param pageId Page ID to delete
 * @returns Promise that resolves when the page is deleted or a ServerError
 */
export async function deletePage(
  eventId: string,
  pageId: string,
): Promise<void | ServerError> {
  try {
    if (
      !eventId ||
      !pageId ||
      !isValidObjectId(eventId) ||
      !isValidObjectId(pageId)
    ) {
      throw new Error("validationError");
    }
    await deletePageApi(eventId, pageId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Update a page with validation checks
 * @param eventId Event ID
 * @param pageId Page ID to update
 * @param page Page object with updated values
 * @param pages Array of all pages for duplicate slug checking
 * @returns Promise that resolves when the update is complete or a ServerError
 */
export async function updatePage(
  eventId: string,
  pageId: string,
  page: Page,
  pages: Page[],
): Promise<void | ServerError> {
  try {
    // Input validation
    if (
      !eventId ||
      !pageId ||
      !page ||
      !isValidObjectId(eventId) ||
      !isValidObjectId(pageId) ||
      (!page.pageName &&
        !page.slug &&
        !page.websiteJson &&
        page.active === undefined &&
        // SEO-only saves are valid (B-6); "" is a deliberate clear.
        page.seoTitle === undefined &&
        page.seoDescription === undefined &&
        page.ogImageUrl === undefined)
    )
      throw new Error("validationError");

    if (page.slug && !/^\/[a-z0-9\-_\/]*$/.test(page.slug))
      throw new Error("validationError");

    // Check for duplicate slugs within the pages array
    if (page.slug !== undefined) {
      const slugs = new Set<string>();
      const duplicateSlugs = pages
        .map((page) => page.slug)
        .filter((slug) => {
          if (slugs.has(slug!)) {
            return true;
          }
          slugs.add(slug!);
          return false;
        });

      if (duplicateSlugs?.length > 0) throw new Error("validationError");
    }


    // Get the actual creating pages call
    await updatePageApi(eventId, pageId, page);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Get a page by ID with validation checks
 * @param eventId Event ID
 * @param pageId Page ID
 * @param fields Optional comma-separated field names to include
 * @returns Promise resolving to the page or a ServerError
 */
export async function getPage(
  eventId: string,
  pageId: string,
  fields?: string,
): Promise<Page | ServerError> {
  try {
    // Input validation
    if (
      !eventId ||
      !pageId ||
      !isValidObjectId(eventId) ||
      !isValidObjectId(pageId)
    ) {
      throw new Error("validationError");
    }

    return await getPageApi(eventId, pageId, fields);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Initialize website builder data by fetching page, pages, and meta page
 * @param eventId Event ID
 * @param slug Page slug to fetch
 * @param fields1 Optional fields for the page
 * @param fields2 Optional fields for all pages
 * @returns Promise resolving to an object with page, pages, and metaPage or a ServerError
 */
export async function initializeWB(
  eventId: string,
  slug: string,
  fields1?: string,
  fields2?: string,
): Promise<
  | {
      page: Page;
      pages: Page[];
      metaPage: WebsiteConfig;
      fields: Field[];
      s3Pictures: string[];
    }
  | ServerError
> {
  try {
    const [page, pages, metaPage, fields, s3Pictures] = await Promise.all([
      getPageBySlug(eventId, slug, fields1),
      getPages(eventId, fields2),
      getWebsiteConfig(eventId),
      getFields("guest", eventId),
      getS3PictureUrls(eventId),
    ]);

    if ("isServerError" in page && page.isServerError === true) return page;
    if ("isServerError" in pages && pages.isServerError === true) return pages;
    // metaPage can be null post-stage-A (200 + empty body) when no
    // website_configs doc exists. This helper's contract is "succeed iff
    // a saved config exists" — treat null as a missing-config error so
    // callers see the same shape as the legacy 404 path.
    if (metaPage === null) return handleErrorServer(new Error("websiteConfigNotFound"));
    if ("isServerError" in metaPage && metaPage.isServerError === true)
      return metaPage;
    if ("isServerError" in fields && fields.isServerError === true)
      return fields;
    if ("isServerError" in s3Pictures && s3Pictures.isServerError === true)
      return s3Pictures;

    return {
      page: page as Page,
      pages: pages as Page[],
      metaPage: metaPage as WebsiteConfig,
      fields: fields as Field[],
      s3Pictures: s3Pictures as string[],
    };
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Initialize overview data by fetching pages and meta page
 * @param eventId Event ID
 * @returns Promise resolving to an object with pages and metaPage or a ServerError
 */
export async function initializeOverview(eventId: string): Promise<
  | {
      pages: Page[];
      metaPage: WebsiteConfig;
    }
  | ServerError
> {
  try {
    const [pages, metaPage] = await Promise.all([
      getPages(
        eventId,
        // Audit fix (overhaul): SEO fields so PEditPage opens with saved values.
        "pageId,pageName,slug,active,seoTitle,seoDescription,ogImageUrl",
      ),
      getWebsiteConfig(
        eventId,
        // W2-F (H23/M19): version + loginBlockEnabled/availableFrom/
        // availableUntil were missing here. EventWebsitePage already wires
        // them (configVersion → If-Match 409 guard, WebsiteCard access-
        // locked banner) but this projection stripped them, leaving both
        // dead: stale saves silently won and the banner never rendered.
        // Audit fix (overhaul): appBar/footer/primaryLanguage power orphan-page
        // detection + add-to-nav; noindex feeds the sharing-settings toggle.
        // Same bug class as the W2-F note above — UI wired, projection stripped.
        // cookie*: the rest of that dialog's cookie block — the switch read
        // "on" for every site until these were projected AND handed to the provider.
        // imprintUrl/privacyPolicyUrl: handed to WBThemeProvider (its refs);
        // the settings dialog's legal links read their own copy on open.
        "bodyFont,headerFont,primaryColor,customDomain,secondaryColor,backgroundColor,rounding,published,homeSlug,favIcon,touchIcon,colorCookieHeader,fontCookieHeader,colorCookieBody,fontCookieBody,backgroundCookieColor,openGraphTitle,openGraphDescription,openGraphImageUrl,customWebsiteUploaded,customWebsiteActive,customWebsiteFileName,version,loginBlockEnabled,availableFrom,availableUntil,appBar,footer,primaryLanguage,noindex,imprintUrl,privacyPolicyUrl,cookieEnabled,cookieHeaderText,cookieBodyText,cookiePolicyUrl,colorCookieButton,colorCookieButtonText,colorCookieLink",
      ),
    ]);

    if ("isServerError" in pages && pages.isServerError === true) return pages;
    // metaPage null = no saved config (200 + empty body post-stage-A).
    // Treat as missing-config error so the page renders SError/empty-state
    // path consistently with the legacy 404 behavior.
    if (metaPage === null) return handleErrorServer(new Error("websiteConfigNotFound"));
    if ("isServerError" in metaPage && metaPage.isServerError === true)
      return metaPage;

    return {
      pages: pages as Page[],
      metaPage: metaPage as WebsiteConfig,
    };
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Get a page by slug with validation checks
 * @param eventId Event ID
 * @param slug URL-friendly identifier
 * @param fields Optional comma-separated field names to include
 * @returns Promise resolving to the page or a ServerError
 */
export async function getPageBySlug(
  eventId: string,
  slug: string,
  fields?: string,
): Promise<Page | ServerError> {
  try {
    // Input validation
    if (
      !eventId ||
      !slug ||
      !isValidObjectId(eventId) ||
      !/^\/[a-z0-9\-_\/]*$/.test(slug)
    ) {
      throw new Error("validationError");
    }

    return await getPageBySlugApi(eventId, slug, fields);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Get all pages for an event with validation checks
 * @param eventId Event ID
 * @param fields Optional comma-separated field names to include
 * @returns Promise resolving to an array of pages or a ServerError
 */
export async function getPages(
  eventId: string,
  fields?: string,
): Promise<Page[] | ServerError> {
  try {
    // Input validation
    if (!eventId || !isValidObjectId(eventId)) {
      throw new Error("validationError");
    }

    return await getPagesApi(eventId, fields);
  } catch (error) {
    return handleErrorServer(error);
  }
}
